/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useEffect, useRef } from 'react';
import { styled } from '@mui/material/styles';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import InputBase from '@mui/material/InputBase';
import customFilterIcon from "../../assets/common/custom-filter.svg";
import "../../styles/common.css"
import { InputLabel } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import ChevronDown from "../../assets/common/chevron-down.svg";
import ChevronUp from "../../assets/common/chevron-up.svg";
import { useLocation } from 'react-router-dom';

// Styling for Bootstrap-like Input
const BootstrapInput = styled(InputBase)(({ theme }) => ({
  "label + &": {
    marginTop: theme.spacing(2),
  },
  "& .MuiInputBase-input": {
    borderRadius: 4,
    height: "40 px",
    position: "relative",
    backgroundColor: theme.palette.background.paper,
    border: "1px solid #E2E2E2",
    fontSize: "14px",
    fontWeight: 400,
    lineHeight: "18px",
    letterSpacing: "0px",
    color: "#393939",
    padding: "10px 6px",
    textAlign: "left",  // Centers text inside input
    alignItems: "center",
    fontFamily: 'var(--font-family)',
    transition: theme.transitions.create(["border-color", "box-shadow"]),
    "&:focus": {
      borderRadius: 4,
    },
  },
}));

type FilterValues = {
  searchText: string;
  filterBy: string;
};

interface DropdownOption {
  label: string;
  value: string;
  options?: { label: string; value: string }[];
}
interface FilterComponentProps {
  onFilter: (filters: FilterValues) => void;
  filterOpenState: {
    isFilterOpen: boolean;
    setIsFilterOpen: React.Dispatch<React.SetStateAction<boolean>>;
  };
  filtervalues: FilterValues;
  dropdownOptions: DropdownOption[];
  setExternal?: React.Dispatch<React.SetStateAction<boolean>>;
  selectedTab?:number | string;
  setTableFilters?: React.Dispatch<React.SetStateAction<{ filterBy: string; searchText: string }[]>>; 
  initialAppliedFilters?: FilterValues[];
  setFiltersModified?: React.Dispatch<React.SetStateAction<boolean>>;
  resetSignal?: number;
  singleFilterMode?: boolean;
}

const Filter: React.FC<FilterComponentProps> = ({
  onFilter,
  filtervalues,
  filterOpenState: { isFilterOpen, setIsFilterOpen },
  dropdownOptions,
  setExternal,
  setTableFilters,
  initialAppliedFilters,
  setFiltersModified,
  resetSignal,
  singleFilterMode,
}) => {
  const [filters, setFilters] = useState<FilterValues>({
    searchText: "",
    filterBy: "",
  });
  const location = useLocation();
  const filterRef = useRef<HTMLDivElement>(null);
  const [appliedFilters, setAppliedFilters] = useState<FilterValues[]>([]);
  const [removedDefaultFilters, setRemovedDefaultFilters] = useState<FilterValues[]>([]);
  const [locationFilters, setLocationFilters] = useState<FilterValues[]>( location.state?.tableFilters || []);
  const isActionItemsPage = location.pathname.includes("/actionItems"); 
  const getDropdownOptions = (): DropdownOption[] => {
    const selectedOption = dropdownOptions.find(
      (option) => option.value === filters.filterBy
    );
    return selectedOption?.options ?? [];
  };
  const handleOpenFilter = () => {
    setIsFilterOpen(true); // Toggle filter visibility
  };

  useEffect(() => {
    const validLocationFilters = locationFilters.filter(
      (f) => f.filterBy?.trim() !== "" && f.searchText?.trim() !== ""
    );
  
    if (
      validLocationFilters.length > 0 &&
      appliedFilters.length === 0
    ) {
      setAppliedFilters(validLocationFilters);
      setLocationFilters(validLocationFilters);
  
      const lastFilter = validLocationFilters[validLocationFilters.length - 1];
      setFilters(lastFilter);
      onFilter(lastFilter);
  
      if (setTableFilters) {
        setTableFilters(validLocationFilters);
      }
  
      // Clear location.state after using it
      window.history.replaceState({}, document.title);
    }
  }, []);
  
  useEffect(() => {
    if (
      initialAppliedFilters &&
      initialAppliedFilters.length > 0 &&
      appliedFilters.length === 0
    ) {
      const filteredDefaults = initialAppliedFilters.filter(
        (defaultFilter) =>
          !removedDefaultFilters.some(
            (removed) =>
              removed.filterBy === defaultFilter.filterBy &&
              removed.searchText === defaultFilter.searchText
          )
      );
      setAppliedFilters(filteredDefaults);
    }
  }, [initialAppliedFilters, removedDefaultFilters]);
  
  useEffect(() => {
    if (initialAppliedFilters && initialAppliedFilters.length > 0) {
      const nonEmptyFilters = initialAppliedFilters.filter(
        (f) => f.filterBy?.trim() !== "" && f.searchText?.trim() !== ""
      );
      setAppliedFilters(nonEmptyFilters);
    }
  }, [initialAppliedFilters]);
  
  const handleReset = () => {
    setFilters({ searchText: "", filterBy: "" });
    setIsFilterOpen(false);
    onFilter({ searchText: "", filterBy: "" });
    if (initialAppliedFilters) {
      setRemovedDefaultFilters(initialAppliedFilters);
    }
    if(setExternal){
      setExternal(true);
    }
    setAppliedFilters([]);
    if (setTableFilters) {
      setTableFilters([]); // Reset table filters to an empty array
    }
  };

  const handleClick = () => {
     if (filters.filterBy && filters.searchText) {
    setAppliedFilters((prev) => {
      let newFilters;
      if (singleFilterMode) {
        newFilters = [filters];
      } else {
      // Remove existing filter with the same filterBy if present
      const updatedFilters = prev.filter(f => f.filterBy !== filters.filterBy);

      // Add the new filter at the end
        newFilters = [...updatedFilters, filters].slice(-dropdownOptions.length);
      }
      // Ensure it does not exceed dropdownOptions length
      return newFilters
      
    });
    onFilter(filters);
    setIsFilterOpen(false);
  }
  };
  const removeFilter = (index: number) => {
    setAppliedFilters((prevFilters) => {
      const removed = prevFilters[index];
      const updatedFilters = prevFilters.filter((_, i) => i !== index);
      setLocationFilters((prev) =>
        prev.filter(
          (f) =>
            !(f.filterBy === removed.filterBy && f.searchText === removed.searchText)
        )
      );
      if (
        initialAppliedFilters &&
        initialAppliedFilters.some(
          (def) =>
            def.filterBy === removed.filterBy &&
            def.searchText === removed.searchText
        )
      ) {
        setRemovedDefaultFilters((prev) => [...prev, removed]);
      }
  
      if (setTableFilters) {
        setTableFilters(updatedFilters); // Ensure table filters update correctly
      }
      if (updatedFilters.length > 0) {
        const lastFilter = updatedFilters[updatedFilters.length - 1]; // Get last applied filter
        setFilters({ searchText: lastFilter.searchText, filterBy: lastFilter.filterBy });
        onFilter(lastFilter); // Apply the latest filter
      } else {
        // No filters left, reset everything
        setFilters({ searchText: "", filterBy: "" });
        onFilter({ searchText: "", filterBy: "" });
      }
      if (setFiltersModified) {
        setFiltersModified(true); // ✅ user modified filters by removing one
      }
  
      return updatedFilters;
    });
  };
  

  const handleCancel =(index:number) => {
    removeFilter(index)
  }
  const handleClose = () =>{
    setIsFilterOpen(false)
  }

  const formatFilterName = (filterBy: string, searchText?: string) => {
    const category = dropdownOptions.find((option) => option.value === filterBy);
    const categoryLabel = category?.label || filterBy;
  
    if (searchText) {
      const matchedOption = category?.options?.find(opt => opt.value === searchText);
      const valueLabel = matchedOption?.label || searchText;
      return `${categoryLabel} - ${valueLabel}`;
    }
  
    return categoryLabel;
  };
  

  useEffect(() => {
    // Conditionally reset filters only when filtervalues are cleared explicitly
    if (filtervalues.searchText === "" && filtervalues.filterBy === "") {
      setFilters({ searchText: "", filterBy: "" });
    }
  }, [filtervalues]);

useEffect(() => {
  if (resetSignal !== undefined && resetSignal > 0) {
    setFilters({ searchText: "", filterBy: "" });
    setAppliedFilters([]);
  }
}, [resetSignal]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (filterRef.current && !filterRef.current.contains(event.target as Node)) {
        setIsFilterOpen(false);
      }
    };
  
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [setIsFilterOpen]);
  return (
    <div ref={filterRef} onMouseDown={(e) => e.stopPropagation()} className="filter-container">
      {/* Toggle Filter Section */}
      <button className="filter-button">
        <img
          className="filter-icon"
          src={customFilterIcon}
          alt="Filter"
          />
        <h3 className='filter-title'>Filter</h3>
        <span className='filter-title filter-count'>({appliedFilters.length})</span>
      </button>
      { isFilterOpen ? (
        <img
        src={ChevronUp}
        alt="Chevron Up"
        width={16}
        height={16}
        onClick={handleClose}
        style={{cursor: "pointer"}}
        />
      ) : (
        <img
        src={ChevronDown}
        alt="Chevron Down"
        width={16}
        height={16}
        onClick={handleOpenFilter}
        style={{cursor: "pointer"}}
        />
      )}
      <div className="selected-filters" style={isActionItemsPage ? { order: -1 } : {}}>
        {appliedFilters.map((filter, index) => (
          <div
            key={index}
            className="filter-pill"
          >
            {formatFilterName(filter.filterBy, filter.searchText)}
            <button
              onClick={(e) => {
                e.stopPropagation(); // Prevent parent div click
                handleCancel(index);
              }}
              className='close-icon'
            >
              x
            </button>
          </div>
        ))}
      </div>

      {isFilterOpen && ( // Use isFilterOpen to conditionally render the filter component
        <div className={`filter-section ${isActionItemsPage ? "action-items-filter" : ""}`}>
          {/* Filter By Dropdown */}
          <FormControl className="filter-dropdown" variant="standard" sx={{ minWidth: "100px" }}>
          <InputLabel id="filter-by-label" shrink>Where</InputLabel>
            <Select
              labelId="filter-by-label"
              value={filters.filterBy}
              onChange={(e) => setFilters({ ...filters, filterBy: e.target.value })}
              IconComponent={(props) => <KeyboardArrowDownIcon {...props} />}
              input={<BootstrapInput />}
              displayEmpty
              renderValue={(selected) => {
                if (!selected) return "Select option";
                const selectedOption = dropdownOptions.find(opt => opt.value === selected);
                return selectedOption ? selectedOption.label : "";
              }}
            >
              {dropdownOptions.map((option) => (
                <MenuItem key={option.value} value={option.value} sx={{fontFamily:'var(--font-family)', fontSize:"14px"}}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Search Input */} 
          {getDropdownOptions().length > 0 ? (
            <FormControl className="filter-dropdown" variant="standard" sx={{ minWidth: "150px" }}>
              <InputLabel id="filter-by-label" shrink >is</InputLabel>
              <Select
                value={filters.searchText}
                onChange={(e) => setFilters({ ...filters, searchText: e.target.value })}
                IconComponent={(props) => <KeyboardArrowDownIcon {...props} />}
                input={<BootstrapInput />}
                displayEmpty
                renderValue={(selected) => {
                  if (!selected) return "Select option";
                  const selectedOption = getDropdownOptions().find(opt => opt.value === selected);
                  if (selectedOption) return selectedOption.label;
                  return selected;
                }}
              >
                {getDropdownOptions().map((option) => (
                  <MenuItem key={option.value} value={option.value} sx={{fontFamily:'var(--font-family)', fontSize:"14px"}}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : (
            <FormControl className="filter-input" variant="standard">
              <InputLabel id="filter-by-label" shrink>is</InputLabel>
              <BootstrapInput
                id="search-textbox"
                value={filters.searchText}
                onChange={(e) => setFilters({ ...filters, searchText: e.target.value })}
              />
            </FormControl>
          )}

          {/* Action Buttons */}
          <div className='filter-actions'>
          <button
            className="filter-cancel-button"
            onClick={handleReset}
            >
            Clear Filters
          </button>
          <button
            className="filter-apply-button"
            onClick={handleClick}
            >
            Apply Filter
          </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Filter;